/**
 * Auth Store - State Management для аутентификации
 * Аналог lib/features/auth/presentation/bloc/auth_bloc.dart
 *
 * Состояния: Unauthenticated, Loading, Authenticated, Error
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '../api/auth.api'
import { agentsAuthApi } from '@/features/agent/api/agents.api'
import type { User, UserSettings, LoginRequest, AuthState } from '../types/auth.types'
import { useRouter } from 'vue-router'
import dayjs from '@/utils/dayjs'
import { i18n } from '@/i18n'
import { resolveLoginError } from '../utils/loginError'
import { isImpersonating, readImpersonation, tokenStorage } from '../tokenStorage'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  // State
  const user = ref<User | null>(null)
  const userSettings = ref<UserSettings | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshTokenValue = ref<string | null>(null)
  const authState = ref<AuthState>('unauthenticated')
  // Сеанс просмотра «глазами агента» этой вкладки (DEV-2400); null — обычная
  // вкладка. Ref, а не голое значение: storeToRefs падает на null-свойстве стора.
  const impersonation = ref(readImpersonation())
  const error = ref<string | null>(null)
  // DEV-1775: подсказка под ошибкой входа (например, путь восстановления).
  const loginErrorHint = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(
    () => authState.value === 'authenticated' && !!accessToken.value && !!user.value,
  )

  const isLoading = computed(() => authState.value === 'loading')

  // Таймзона менеджера: профиль → guess браузера → UTC.
  const userTimezone = computed(() => {
    const tz = userSettings.value?.timezone
    if (tz) return tz
    try {
      return dayjs.tz.guess() || 'UTC'
    } catch {
      return 'UTC'
    }
  })

  const loadUserSettings = async () => {
    try {
      userSettings.value = await authApi.getUserSettings()
    } catch (err) {
      console.error('Failed to load user settings:', err)
    }
  }

  // Actions

  /**
   * Login - аналог LoginUser usecase
   */
  const login = async (credentials: LoginRequest) => {
    authState.value = 'loading'
    error.value = null
    loginErrorHint.value = null

    try {
      const response = await authApi.login(credentials)

      // Save tokens
      accessToken.value = response.tokens.accessToken
      refreshTokenValue.value = response.tokens.refreshToken
      user.value = response.user
      authState.value = 'authenticated'

      // Persist to localStorage
      tokenStorage.setTokens(response.tokens.accessToken, response.tokens.refreshToken)
      tokenStorage.setUser(response.user)

      await loadUserSettings()

      // Navigate to dashboard
      router.push('/dashboard')
    } catch (err: unknown) {
      authState.value = 'error'
      // DEV-1775: человеку — причина отказа, а не «Request failed with status code 403».
      const resolved = resolveLoginError(err)
      error.value = 'key' in resolved ? i18n.global.t(resolved.key) : resolved.text
      loginErrorHint.value =
        'hintKey' in resolved && resolved.hintKey ? i18n.global.t(resolved.hintKey) : null
      throw err
    }
  }

  /**
   * Logout - аналог LogoutUser usecase
   *
   * redirect: false нужен вызову из навигационного гварда — там переход задаёт
   * сам гвард через next(), и router.push() отсюда отменил бы его как
   * конкурирующую навигацию (DEV-1177).
   */
  const logout = async (options: { redirect?: boolean } = {}) => {
    const { redirect = true } = options

    // Во вкладке просмотра «выйти» — значит закончить просмотр: сеанс самого
    // агента на его устройствах трогать нельзя (DEV-2400).
    if (isImpersonating()) {
      await endImpersonation()
      return
    }

    try {
      await authApi.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      // Clear state
      user.value = null
      userSettings.value = null
      accessToken.value = null
      refreshTokenValue.value = null
      authState.value = 'unauthenticated'
      error.value = null
      loginErrorHint.value = null

      // Clear localStorage
      tokenStorage.clear()

      // Navigate to login
      if (redirect) {
        router.push('/login')
      }
    }
  }

  /**
   * Refresh Token - аналог RefreshToken usecase
   */
  const refreshToken = async () => {
    if (!refreshTokenValue.value) {
      throw new Error('No refresh token available')
    }

    try {
      const response = await authApi.refreshToken(refreshTokenValue.value)

      accessToken.value = response.tokens.accessToken
      refreshTokenValue.value = response.tokens.refreshToken

      // Update localStorage
      tokenStorage.setTokens(response.tokens.accessToken, response.tokens.refreshToken)
    } catch (err) {
      // Refresh failed - logout
      await logout()
      throw err
    }
  }

  /**
   * setTokens — синхронная установка пары токенов. Вызывается из axios-интерсептора
   * после фонового refresh, чтобы реактивный стор не отставал от localStorage
   * (accessToken читают напрямую, напр. usePresentationDownload).
   */
  const setTokens = (newAccessToken: string, newRefreshToken: string) => {
    accessToken.value = newAccessToken
    refreshTokenValue.value = newRefreshToken
  }

  /**
   * Initialize - load from localStorage (аналог AppStarted event в AuthBloc)
   */
  const initialize = async () => {
    const storedAccessToken = tokenStorage.getAccessToken()
    const storedRefreshToken = tokenStorage.getRefreshToken()
    const storedUser = tokenStorage.getUser()

    // У вкладки просмотра refresh-токена нет по устройству (DEV-2400).
    if (storedAccessToken && (storedRefreshToken || isImpersonating())) {
      accessToken.value = storedAccessToken
      refreshTokenValue.value = storedRefreshToken

      // Загружаем сохранённого пользователя из localStorage
      if (storedUser) {
        try {
          user.value = JSON.parse(storedUser)
        } catch (err) {
          console.error('Failed to parse stored user:', err)
        }
      }

      authState.value = 'loading'

      try {
        // Get current user from API and update localStorage
        const currentUser = await authApi.getCurrentUser()
        user.value = currentUser
        tokenStorage.setUser(currentUser)
        authState.value = 'authenticated'
        await loadUserSettings()
      } catch (err) {
        // Token invalid - logout
        console.error('Failed to get current user:', err)
        await logout()
      }
    } else {
      authState.value = 'unauthenticated'
    }
  }

  /**
   * Принятие приглашения (DEV-1308): заводит учётку по токену из письма и
   * оставляет агента внутри — сервер отдаёт пару токенов сразу.
   *
   * Сессия ставится через initialize(): он уже тянет /me, настройки и статус,
   * а при негодном токене сам делает logout. Повторять здесь тело login()
   * значило бы завести второе определение того, что такое «вошёл».
   */
  const acceptInvitation = async (payload: {
    token: string
    firstName: string
    lastName: string
    password: string
  }) => {
    const tokens = await agentsAuthApi.acceptInvitation(payload)

    tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken)

    await initialize()

    router.push('/dashboard')
  }

  /**
   * Завершает просмотр «глазами агента» (DEV-2400): сервер гасит сеанс, вкладка
   * забывает его токен и закрывается. Если браузер закрыть её не даёт (вкладку
   * открыл не скрипт), остаётся экран «Сеанс просмотра завершён»: сеанса
   * администратора на домене кабинета нет, возвращаться не к чему.
   */
  const endImpersonation = async () => {
    try {
      await authApi.endImpersonation()
    } catch (err) {
      // Сеанс мог уже истечь — забыть токен вкладки всё равно нужно.
      console.error('End impersonation error:', err)
    } finally {
      tokenStorage.clear()
      window.close()
      await router.replace({ name: 'impersonation-ended' })
    }
  }

  /**
   * Clear error
   */
  const clearError = () => {
    error.value = null
    loginErrorHint.value = null
  }

  return {
    // State
    user,
    userSettings,
    accessToken,
    authState,
    error,
    loginErrorHint,
    impersonation,

    // Getters
    isAuthenticated,
    isLoading,
    userTimezone,

    // Actions
    login,
    logout,
    refreshToken,
    setTokens,
    initialize,
    acceptInvitation,
    endImpersonation,
    clearError,
    loadUserSettings,
  }
})
